import { collection, onSnapshot, type QuerySnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { createSharedSubscription } from "@/lib/firestore/createSharedSubscription";
import { toMillis } from "@/lib/timestamps";
import { isImpersonationActive } from "@/stores/auth-store";
import { reportError } from "@/lib/report-error";
import type { Guest } from "@amazing-wedding/shared-types";
import { maybeBackfillAttendeeCount } from "@/features/guests/sync-attendee-count";

function parseGuests(snap: QuerySnapshot): Guest[] {
  const rows: Guest[] = [];
  snap.forEach((d) => {
    const data = d.data() as Omit<Guest, "id">;
    rows.push({
      id: d.id,
      ...data,
      plusOne: data.plusOne ?? null,
      children: data.children ?? [],
      plusOneAllowed: data.plusOneAllowed ?? false,
      rsvpToken: data.rsvpToken ?? null,
    });
  });
  rows.sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
  return rows;
}

export const guestsSubscription = createSharedSubscription<Guest[]>({
  getDefault: () => [],
  attach: (weddingId, setSnapshot) => {
    setSnapshot({ data: [], loading: true });
    return onSnapshot(
      collection(db, "weddings", weddingId, "guests"),
      (snap) => {
        const guests = parseGuests(snap);
        setSnapshot({ data: guests, loading: false });
        // Read-only while impersonating — the admin can't write this wedding.
        if (!isImpersonationActive()) {
          void maybeBackfillAttendeeCount(weddingId, guests).catch((err) =>
            reportError("attendee-backfill", err),
          );
        }
      },
      () => setSnapshot({ data: [], loading: false }),
    );
  },
});
